import { CHAIN } from '@tonconnect/ui-react';

/**
 * Serving origin of this Mini App. Production bakes `VITE_WEBAPP_ORIGIN` at
 * build; dev (and the shooter on :5199) uses the page origin so a production
 * host is not required to load `/gallery`.
 */
export function webappOrigin(): string {
  const configured = import.meta.env.VITE_WEBAPP_ORIGIN;
  if (configured) return configured;
  if (import.meta.env.DEV) return window.location.origin;
  throw new Error('VITE_WEBAPP_ORIGIN is missing from this production bundle');
}

export function tonConnectManifestUrl(): string {
  return `${webappOrigin()}/tonconnect-manifest.json`;
}

/**
 * Mini App link wallets use to return after approval. Production bakes
 * `VITE_TWA_RETURN_URL` at build; unset in DEV so `/gallery` and the shooter
 * load without a BotFather link.
 */
export function twaReturnUrl(): `${string}://${string}` | undefined {
  const configured = import.meta.env.VITE_TWA_RETURN_URL;
  if (configured) return configured as `${string}://${string}`;
  if (import.meta.env.DEV) return undefined;
  throw new Error('VITE_TWA_RETURN_URL is missing from this production bundle');
}

const NANOTON = 1_000_000_000;

function toncenterUrl(chain: string): string {
  if (chain === CHAIN.TESTNET) return 'https://testnet.toncenter.com/api/v2/getAddressBalance';
  if (chain === CHAIN.MAINNET) return 'https://toncenter.com/api/v2/getAddressBalance';
  throw new Error(`unknown chain ${chain}`);
}

/** Wallet-row truncation. Full address lives on the wallet sheet. */
export function shortAddress(address: string): string {
  if (address.length <= 12) return address;
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

export function networkLabel(chain: string): string {
  if (chain === CHAIN.TESTNET) return 'testnet';
  if (chain === CHAIN.MAINNET) return 'mainnet';
  return chain;
}

/** Honest display — never rounds a real sub-1 balance down to 0. */
export function formatTon(ton: number): string {
  if (ton === 0) return '0';
  const abs = Math.abs(ton);
  if (abs < 0.01) return ton.toLocaleString('en-US', { maximumFractionDigits: 4 });
  return ton.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/** Live chain read. Throws on failure — callers must not coerce that into 0. */
export async function fetchTonBalance(
  address: string,
  chain: string,
  signal?: AbortSignal,
): Promise<number> {
  const url = `${toncenterUrl(chain)}?address=${encodeURIComponent(address)}`;
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`balance http ${res.status}`);
  const body = (await res.json()) as { ok?: boolean; result?: string; error?: string };
  if (!body.ok || body.result == null || body.result === '') {
    throw new Error(body.error ?? 'balance unread');
  }
  if (!/^\d+$/.test(body.result)) throw new Error('balance malformed');
  return Number(BigInt(body.result)) / NANOTON;
}
